import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import {
    TableHead,
    TableSortLabel,
    TableRow,
    TableCell,
    Link,
    Box,
    styled,
    Table,
    Typography,
    Collapse,
    InputBase,
} from '@mui/material'
import {
    Tooltip,
    StatusIndicator,
    RiskIndicator,
    Select,
} from 'packages/eid-ui'
import { usePageContext } from 'pageContext'
import { useTranslation } from 'react-i18next'
import {
    TextFormatter,
    Person,
    ItemTypeCount,
    SubjectTypeAttribute,
    Button,
} from 'components'
import { useHistory } from 'react-router-dom'
import { useQuery } from 'packages/core'
import moment from 'moment'
import { getFullImageUrl } from 'utils'
import RowStatus from 'components/RowStatus'
import DynamicComponents from 'components/DynamicComponents'
import ShowHideButton from 'components/Button/ShowHideButton'
import { Icon } from 'packages/eid-icons'
import InboxStatusIndicator from 'components/InboxStatusIndicator'

const maxCharacters = 100

const colors = {
    primary: '#0057A0',
    grey: '#7d7c7c',
    black: '#282828',
}

const EmptyRow = styled(TableRow)({
    border: 'none !important',
})
const EmptyCell = styled(TableCell)({
    border: 'none !important',
    padding: '9px',
    backgroundColor: 'transparent !important',
})

const HeaderRow = styled(TableRow)(({ theme }) => ({
    height: '32px',
    borderRadius: '8px !important',
    boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.05)',

    '& >th:first-child': {
        borderBottomLeftRadius: '4px !important',
        borderTopLeftRadius: '4px !important',
    },
    '& >th:last-child': {
        borderBottomRightRadius: '4px !important',
        borderTopRightRadius: '4px !important',
    },
    '& .MuiTableCell-head': {
        padding: '10px',
        fontSize: '13px',
        lineHeight: 1.33,
        color: colors.grey,
    },
}))

const HeaderCell = styled(TableCell)(({ style }) => ({
    border: 'none !important',
    padding: '16px 10px',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
    // overflow: 'hidden',
    ...style,
}))

const StyledTableCell = styled(TableCell)(({ style }) => ({
    border: 'none',
    borderRight: '1px solid #e8e8e8',
    padding: '16px 0px',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
    // overflow: 'hidden',
    color: colors.black,
    fontSize: '13px',
    lineHeight: 1.5,
    position: 'relative',
    '& >a': {
        color: style?.color || colors.primary,
    },
    '&:last-child': {
        borderRight: 'none',
    },

    ...style,
}))

// Active row border colors (gradient simulation)
const activeBorderColors = {
    left: '#682a94',
    right: '#05afe0',
    top: 'linear-gradient(to right, #682a94 0%, #05afe0 100%)',
}

const StyledRow = styled(TableRow)(({ theme }) => ({
    backgroundColor: '#ffffff !important',
    cursor: 'pointer',
    borderTopLeftRadius: '8px !important',
    borderTopRightRadius: '8px !important',
    minHeight: '40px',

    '& >td:first-child': {
        borderBottomLeftRadius: '0px !important',
        borderTopLeftRadius: '8px !important',
    },

    '&:focus-visible': {
        outline: `2px solid ${theme?.palette?.primary?.main}`,
        outlineOffset: '2px',
    },
    '& >td:last-child': {
        borderBottomRightRadius: '0px !important',
        borderTopRightRadius: '8px !important',
    },

    '&:hover .Details-button': {
        backgroundColor: colors.primary,
        color: '#ffffff',
        cursor: 'pointer',
    },
}))

const StyledSecondRow = styled(TableRow)({
    backgroundColor: '#ffffff',
    minHeight: '40px',
    boxShadow: 'inset 0 6px 16px -16px rgba(0, 0, 0, 0.5)',

    '& >td:first-child': {
        borderBottomLeftRadius: '8px !important',
        borderTopLeftRadius: '0px !important',
    },
    '& >td:last-child': {
        borderBottomRightRadius: '8px !important',
        borderTopRightRadius: '0px !important',
    },

    '&:hover .Details-button': {
        backgroundColor: colors.primary,
        color: '#ffffff',
        cursor: 'pointer',
    },
})

const tooltipProps = {
    color: '#5D6870',
    fontColor: '#ffffff',
    enterDelay: 500,
    enterNextDelay: 500,
    disableInteractive: false,
    tooltipStyles: {
        boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.11)',
        padding: '8px',
        maxWidth: '500px',
    },
}

const CustomTableCell = ({ style, link, children, onClick, url, ...rest }) => {
    return (
        <StyledTableCell align="left" style={style} onClick={onClick} {...rest}>
            {!link ? (
                children
            ) : (
                <Link
                    href={url}
                    onClick={(e) => {
                        e.stopPropagation()
                    }}
                >
                    {children}
                </Link>
            )}
        </StyledTableCell>
    )
}

// ✅ Sticky helpers (NEW)
const getNumericPx = (value) => {
    if (value == null) return 0
    if (typeof value === 'number') return value
    if (typeof value !== 'string') return 0
    const v = value.trim()
    if (v.endsWith('px')) return parseFloat(v) || 0
    // If width is provided but not px (e.g. '12rem' / '%'), we can’t reliably compute offsets.
    // For sticky columns, use explicit pixel widths.
    const parsed = parseFloat(v)
    return Number.isFinite(parsed) ? parsed : 0
}

const buildStickyMeta = (attributes) => {
    const stickyStart = attributes
        .map((a, i) => ({ a, i }))
        .filter(({ a }) => a?.sticky === 'start')
    const stickyEnd = attributes
        .map((a, i) => ({ a, i }))
        .filter(({ a }) => a?.sticky === 'end')

    // Sort by stickyOrder if provided, otherwise keep DOM order.
    stickyStart.sort((x, y) => {
        const ax = x.a?.stickyOrder
        const ay = y.a?.stickyOrder
        if (ax == null && ay == null) return x.i - y.i
        if (ax == null) return 1
        if (ay == null) return -1
        return ax - ay
    })

    stickyEnd.sort((x, y) => {
        const ax = x.a?.stickyOrder
        const ay = y.a?.stickyOrder
        if (ax == null && ay == null) return y.i - x.i // for right offsets we compute from rightmost inward
        if (ax == null) return 1
        if (ay == null) return -1
        return ax - ay
    })

    const startOffsetsByIndex = {}
    const endOffsetsByIndex = {}

    let left = 0
    stickyStart.forEach(({ a, i }) => {
        startOffsetsByIndex[i] = left
        const w = getNumericPx(a?.styles?.width) || 0
        left += w
    })

    let right = 0
    // For end, we want the rightmost sticky cell to have right=0, next right=width(rightmost), etc.
    // So iterate in DOM order from the end-side perspective (rightmost first).
    // We already sorted stickyEnd for that; but if stickyOrder used, caller should define it clearly.
    stickyEnd
        .slice()
        .sort((x, y) => y.i - x.i)
        .forEach(({ a, i }) => {
            endOffsetsByIndex[i] = right
            const w = getNumericPx(a?.styles?.width) || 0
            right += w
        })

    return { startOffsetsByIndex, endOffsetsByIndex }
}

const getStickyCellSx = ({
    isStickyStart,
    isStickyEnd,
    left,
    right,
    zIndex,
}) => {
    if (!isStickyStart && !isStickyEnd) return {}

    return {
        position: 'sticky',
        ...(isStickyStart ? { left } : {}),
        ...(isStickyEnd ? { right } : {}),
        zIndex,
        backgroundColor: '#ffffff',
        // a subtle separator so content under sticky columns doesn’t visually blend
        boxShadow: isStickyStart
            ? '6px 0 10px -10px rgba(0,0,0,0.3)'
            : isStickyEnd
            ? '-6px 0 10px -10px rgba(0,0,0,0.3)'
            : undefined,
    }
}

export const EditableGridTableHead = ({ attributes }) => {
    const { t } = useTranslation()
    const [{ sortBy, sortOrder }, dispatch] = usePageContext()

    // ✅ compute sticky offsets once per attributes change (NEW)
    const stickyMeta = useMemo(() => buildStickyMeta(attributes), [attributes])

    const handleSort = (heading) => {
        const sortByName = heading.sortBy ? heading.sortBy : heading.name
        dispatch({
            type: 'SET_MULTIPLE_PROPS',
            payload: {
                sortBy: sortByName,
                sortOrder:
                    sortBy !== sortByName
                        ? 'desc'
                        : !sortOrder || sortOrder === '' || sortOrder === 'asc'
                        ? 'desc'
                        : 'asc',
            },
        })
    }

    return (
        <TableHead variant="eidHead" style={{ backgroundColor: 'transparent' }}>
            <>
                <HeaderRow>
                    {attributes.map((heading, index) => {
                        const sortByName = heading.sortBy
                            ? heading.sortBy
                            : heading.name

                        const isStickyStart = heading?.sticky === 'start'
                        const isStickyEnd = heading?.sticky === 'end'
                        const left = stickyMeta.startOffsetsByIndex[index] ?? 0
                        const right = stickyMeta.endOffsetsByIndex[index] ?? 0

                        return (
                            <HeaderCell
                                key={'pd-head-cell-' + index}
                                style={heading.headerCellStyles}
                                align="left"
                                sx={{
                                    ...getStickyCellSx({
                                        isStickyStart,
                                        isStickyEnd,
                                        left,
                                        right,
                                        zIndex: 4, // header should be above body sticky cells
                                    }),
                                    backgroundColor: '#ffffff',
                                }}
                            >
                                {!heading.sortable ? (
                                    <span style={{ display: 'flex' }}>
                                        {t(heading.label)}
                                    </span>
                                ) : (
                                    <span style={{ display: 'flex' }}>
                                        <Tooltip
                                            title={
                                                sortBy === sortByName &&
                                                sortOrder === 'desc'
                                                    ? t('SortAscending')
                                                    : t('SortDescending')
                                            }
                                        >
                                            <TableSortLabel
                                                active={sortBy === sortByName}
                                                direction={
                                                    sortBy === sortByName
                                                        ? sortOrder
                                                        : 'asc'
                                                }
                                                onClick={() =>
                                                    handleSort(heading)
                                                }
                                            >
                                                {t(heading.label)}
                                            </TableSortLabel>
                                        </Tooltip>
                                    </span>
                                )}
                            </HeaderCell>
                        )
                    })}
                </HeaderRow>
                <EmptyRow>
                    <EmptyCell colSpan={attributes.length} />
                </EmptyRow>
            </>
        </TableHead>
    )
}

const DetailsButton = ({ styles, onClick }) => (
    <span style={styles}>
        <StyledLink
            className="Details-button"
            component="button"
            onClick={(e) => {
                onClick()
                e.stopPropagation()
            }}
        >
            +
        </StyledLink>
    </span>
)

const StyledLink = styled(Link)({
    borderRadius: '50%',
    fontWeight: 'normal',
    width: '20px',
    height: '20px',
    backgroundColor: '#ffffff',
    border: '1px solid #aab0b4',
    color: '#aab0b4',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',

    '&:hover': {
        backgroundColor: colors.primary,
        color: '#ffffff',
        cursor: 'pointer',
    },

    '&:focus': {
        outline: 'none',
    },
})

const SecondRowCell = styled(TableCell)({
    padding: '10px 10px',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
    overflow: 'hidden',
    color: colors.grey,
    borderRadius: '8px !important',
    borderImageSource:
        'linear-gradient(to right, rgba(120, 48, 197, 0), #7830c5 7%, #c530a9 91%, rgba(197, 48, 169, 0))',
    borderImageSlice: 1,
    //boxShadow: 'inset 0 0 10px -10px #000000',
})

const CellDateTime = ({ value }) => (
    <>{value ? moment.utc(value).local().format('LLL') : '-'}</>
)

const Attribute = styled('span')({
    display: 'flex',
    alignItems: 'center',
    padding: '0 10px',
    fontSize: '13px !important',
    '& >span': {
        margin: '0px 6px 0px 10px',
        fontWeight: 'bold',
    },
    '& >svg': {
        marginBottom: '1px',
    },
})
const StyledTable = styled(Table)({
    border: 'none !important',
    boxShadow: 'none !important',
    borderCollapse: 'separate',

    // ✅ keep outer width stable
    width: '100%',

    // ✅ allow columns to expand beyond container (scroll inside wrapper)
    minWidth: 'max-content',

    tableLayout: 'auto',
})
export const StyledNonSortingLabel = styled(Typography)(() => ({
    display: 'flex',
    fontSize: '1.4rem',
}))

export const EditableGridTable = StyledTable

// Editable Input Styles
const EditableInput = styled(InputBase)(({ theme }) => ({
    width: '100%',
    '& input': {
        borderRadius: '4px',
        backgroundColor: '#ffffff',
        border: '1px solid #e8e8e8',
        fontSize: '13px',
        padding: '8px 12px',
        transition:
            'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
        '&:focus': {
            borderColor: colors.primary,
            boxShadow: `0 0 0 2px ${colors.primary}20`,
        },
        '&::placeholder': {
            opacity: 1,
            color: '#b4b4b4',
        },
    },
}))

// Expandable Content Cell
const ExpandableContentCell = styled(TableCell)({
    padding: '0px',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
    // overflow must remain visible so that position:sticky on the inner content
    // can find the scroll container ancestor (overflow:hidden would block it)
    overflow: 'visible',
    color: colors.grey,
    border: 'none',
    // Border-radius lives on the sticky Box inside, not here, because this cell
    // spans the full scrollable table width — any decoration here would be
    // clipped or shifted as the table scrolls.
})

// Expandable Row
const ExpandableRow = styled(TableRow)({
    backgroundColor: '#ffffff !important',
    cursor: 'default',
    minHeight: '40px',

    '& >td:first-child': {
        borderBottomLeftRadius: '8px !important',
        borderTopLeftRadius: '0px !important',
    },
    '& >td:last-child': {
        borderBottomRightRadius: '8px !important',
        borderTopRightRadius: '0px !important',
    },
})

// Modified Label
const ModifiedLabel = styled(Box)(({ isSaved }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px',
    borderBottomRightRadius: '4px',
    backgroundColor: isSaved ? '#0057A0' : '#d66a00',
    color: '#ffffff',
    fontSize: '9px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '4px',
    position: 'absolute',
    top: '0',
    left: '0',
}))

// Save Button
const SaveButton = styled(Box)({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 12px',
    borderRadius: '4px',
    backgroundColor: '#01ae8f',
    color: '#ffffff',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '&:hover': {
        backgroundColor: '#019a7e',
    },
})

// InvalidAwareDropdown — always shows the Select, but when the current value
// is not found in the predefined options, displays it as-is inside the closed
// dropdown and adds a red border so the user knows it needs to be corrected.
// The dropdown list only ever shows valid options.
const InvalidAwareDropdown = ({
    fieldName,
    currentValue,
    editableConfig,
    attribute,
    onDropdownChange,
}) => {
    const { t } = useTranslation()

    const selectedOption =
        editableConfig.options?.find((opt) => opt.id === currentValue) || null
    const isInvalid = Boolean(currentValue) && !selectedOption

    // When invalid, pass a synthetic option so the raw text is rendered inside
    // the closed Select. This option is NOT added to the options list, so it
    // never appears in the open dropdown — only valid options are shown there.
    const displayValue = isInvalid
        ? { id: currentValue, label: currentValue }
        : selectedOption

    return (
        <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
                width: '100%',
                ...(isInvalid && {
                    outline: '1px solid #d32f2f',
                    borderRadius: '4px',
                }),
            }}
        >
            <Select
                value={displayValue}
                options={editableConfig.options || []}
                placeholder={editableConfig.placeholder || t(attribute.label)}
                getOptionLabel={
                    editableConfig.getOptionLabel ||
                    ((opt) => opt?.label || '')
                }
                onChange={(option) => {
                    onDropdownChange(fieldName, option?.id || '')
                }}
                menuItemStyles={{ fontSize: '1.4rem' }}
            />
        </Box>
    )
}

export const EditableGridRow = ({
    attributes,
    item,
    isRowClickable,
    expandable = false,
    expandableComponent,
    onRowChange,
    onStatusChange,
    updatingItemId,
    isRowEditable,
    onSave,
    savingItemId,
    savedItemId,
    onClearSaved,
    itemIdProp = 'rtA_GroupToBusinessRoleID',
    isFetching = false, // True when parent is refetching data
    ...rest
}) => {
    const { t } = useTranslation()
    const history = useHistory()
    const query = useQuery()
    const [showDetails, setShowDetails] = useState(false)
    const [editedValues, setEditedValues] = useState({}) // Pending changes (not yet saved)
    const [savedValues, setSavedValues] = useState({}) // Values that have been saved but not yet refetched

    // ✅ compute sticky offsets once per attributes change (NEW)
    const stickyMeta = useMemo(() => buildStickyMeta(attributes), [attributes])

    // Determine if row is expandable (either via prop or if expandableComponent is provided)
    // Declared early so the ResizeObserver effect below can reference it.
    const isExpandable = expandable || !!expandableComponent

    // Ref on the expandable row to locate the scroll container and measure its width.
    // This lets the expandable content pin to the visible viewport instead of
    // stretching across the full (scrollable) table width.
    const expandableRowRef = useRef(null)
    const [scrollContainerWidth, setScrollContainerWidth] = useState(null)

    useEffect(() => {
        if (!isExpandable) return
        const el = expandableRowRef.current
        if (!el) return

        // Walk up the DOM to find the nearest horizontally scrollable ancestor
        let parent = el.parentElement
        while (parent && parent !== document.documentElement) {
            const overflowX = getComputedStyle(parent).overflowX
            if (overflowX === 'auto' || overflowX === 'scroll') break
            parent = parent.parentElement
        }
        if (!parent || parent === document.documentElement) return

        const measure = () => setScrollContainerWidth(parent.clientWidth)
        const ro = new ResizeObserver(measure)
        ro.observe(parent)
        measure()
        return () => ro.disconnect()
    }, [isExpandable])

    // Ref for the expandable content box — used to block horizontal wheel scroll
    // from reaching the table scroll container while the cursor is over it.
    const expandableContentRef = useRef(null)

    useEffect(() => {
        const el = expandableContentRef.current
        if (!el) return

        const handleWheel = (e) => {
            // Block horizontal scroll so the table doesn't pan beneath the panel.
            // Vertical delta is left untouched so the page can still scroll normally.
            if (Math.abs(e.deltaX) > 0) {
                e.stopPropagation()
                e.preventDefault()
            }
        }

        // { passive: false } is required to be able to call preventDefault()
        el.addEventListener('wheel', handleWheel, { passive: false })
        return () => el.removeEventListener('wheel', handleWheel)
    }, [])

    // Track previous isFetching state to detect when refetch completes
    const wasFetchingRef = useRef(isFetching)

    // Clear savedValues when refetch completes (isFetching goes from true to false)
    useEffect(() => {
        if (
            wasFetchingRef.current &&
            !isFetching &&
            Object.keys(savedValues).length > 0
        ) {
            // Refetch just completed - clear savedValues so fresh item data is used
            setSavedValues({})
        }
        wasFetchingRef.current = isFetching
    }, [isFetching, savedValues])

    // Track if this specific item is being saved or was just saved
    const isSaving = savingItemId === item[itemIdProp]
    // Check if this item was saved (from parent state - persists across remounts)
    const wasSavedFromParent = savedItemId === item[itemIdProp]
    // Show saved only if item was saved AND no pending modifications
    const hasSavedValues = Object.keys(savedValues).length > 0
    const hasPendingChanges = Object.keys(editedValues).length > 0
    // Use either local savedValues or parent's savedItemId to show "Saved" badge
    const isSaved = (hasSavedValues || wasSavedFromParent) && !hasPendingChanges

    const toggleShowDetails = () => {
        setShowDetails((prev) => !prev)
    }

    const handleRowClick = () => {
        if (isRowClickable) {
            query.set('id', item.id)
            history.push(`${history.location.pathname}?${query.toString()}`)
        }
    }

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            handleRowClick()
        }
    }

    // Handle editable field change (just updates local state, doesn't trigger save)
    const handleEditableChange = useCallback(
        (fieldName, value) => {
            setEditedValues((prev) => ({
                ...prev,
                [fieldName]: value,
            }))
            // Notify parent of the change (for tracking purposes)
            if (onRowChange) {
                onRowChange(item, fieldName, value)
            }
        },
        [item, onRowChange],
    )

    // Get the current value for a field (edited values take priority, then item data)
    // Note: We intentionally don't use savedValues here - it's only for the "Saved" label
    // This ensures fresh data from refetch is always displayed
    const getFieldValue = useCallback(
        (fieldName) => {
            if (editedValues.hasOwnProperty(fieldName)) {
                return editedValues[fieldName]
            }
            return item[fieldName]
        },
        [editedValues, item],
    )

    // Move value from editedValues to savedValues on successful save
    const markFieldAsSaved = useCallback((fieldName, value) => {
        // Remove from editedValues
        setEditedValues((prev) => {
            const newState = { ...prev }
            delete newState[fieldName]
            return newState
        })
        // Add to savedValues
        setSavedValues((prev) => ({
            ...prev,
            [fieldName]: value,
        }))
    }, [])

    // Trigger save to parent - called on blur for text fields, immediately for dropdowns
    const triggerSave = useCallback(
        (fieldName, value) => {
            if (onSave) {
                // Only send modified fields + the item ID
                const payload = {
                    [itemIdProp]: item[itemIdProp],
                    [fieldName]: value,
                }
                onSave(payload, item, () => {
                    // On success - move value from editedValues to savedValues
                    markFieldAsSaved(fieldName, value)
                })
            }
        },
        [item, itemIdProp, onSave, markFieldAsSaved],
    )

    // Handle blur for text inputs - trigger save
    const handleBlur = useCallback(
        (fieldName, value) => {
            // Only trigger save if value has actually changed from original/saved
            const currentSavedOrOriginal = savedValues.hasOwnProperty(fieldName)
                ? savedValues[fieldName]
                : item[fieldName]
            if (currentSavedOrOriginal !== value) {
                triggerSave(fieldName, value)
            }
        },
        [item, savedValues, triggerSave],
    )

    // Handle dropdown change - update local state and immediately trigger save
    const handleDropdownChange = useCallback(
        (fieldName, value) => {
            // Update local state first
            setEditedValues((prev) => ({
                ...prev,
                [fieldName]: value,
            }))
            // Check if value has actually changed from original/saved
            const currentSavedOrOriginal = savedValues.hasOwnProperty(fieldName)
                ? savedValues[fieldName]
                : item[fieldName]
            // Immediately trigger save - only send modified field + item ID
            if (onSave && currentSavedOrOriginal !== value) {
                const payload = {
                    [itemIdProp]: item[itemIdProp],
                    [fieldName]: value,
                }
                onSave(payload, item, () => {
                    // On success - move value from editedValues to savedValues
                    markFieldAsSaved(fieldName, value)
                })
            }
        },
        [item, itemIdProp, savedValues, onSave, markFieldAsSaved],
    )

    // Check if row has been modified (local changes not yet saved)
    const isRowModified = Object.keys(editedValues).length > 0

    // Render editable controls based on component type
    const renderEditableControls = (a, item) => {
        const fieldName = a.name
        const currentValue = getFieldValue(fieldName)
        const editableConfig = a.editableConfig || {}
        const componentType = editableConfig.type || 'text'

        switch (componentType) {
            case 'dropdown':
                return (
                    <InvalidAwareDropdown
                        fieldName={fieldName}
                        currentValue={currentValue}
                        editableConfig={editableConfig}
                        attribute={a}
                        onDropdownChange={handleDropdownChange}
                    />
                )
            case 'text':
            default:
                return (
                    <EditableInput
                        value={currentValue || ''}
                        placeholder={editableConfig.placeholder || t(a.label)}
                        onChange={(e) =>
                            handleEditableChange(fieldName, e.target.value)
                        }
                        onBlur={(e) => handleBlur(fieldName, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        inputProps={{
                            'aria-label': t(a.label),
                        }}
                    />
                )
        }
    }

    const renderButton = (a) => {
        switch (a.button?.name) {
            case 'DetailsButton':
                return (
                    <DetailsButton
                        onClick={() => {}}
                        styles={a.button.styles}
                    />
                )
            case 'ShowHideButton':
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            width: 'max-content',
                        }}
                    >
                        <ShowHideButton
                            showLabel={'More'}
                            hideLabel={'Less'}
                            onClick={toggleShowDetails}
                            expanded={showDetails}
                        />
                    </Box>
                )
            default:
                return <></>
        }
    }

    const getItemStatus = (processStatusName) => {
        switch (processStatusName) {
            case 'Not Processed':
                return 'NotStarted'
            case 'In Progress':
                return 'Open'
            case 'Processed':
                return 'Completed'
            case 'Error':
                return 'Error'
            case 'Ignored':
                return 'Deferred'
            case 'Pending Reprocessing':
                return 'Waiting'
            case 'Canceled':
                return 'Canceled'
            default:
                return 'Default'
        }
    }

    const renderComponent = (a, item) => {
        switch (a.component?.componentName) {
            case 'StatusIndicator':
                const itemStatus = getItemStatus(item.processStatusName)

                return (
                    <StatusIndicator
                        className="Status-Indicator"
                        style={a.component.styles}
                        variant={itemStatus}
                        label={t(`${item.processStatusName}`)}
                        {...a.component.props}
                    />
                )
            case 'InboxStatusIndicator':
                const statusChangeConfig = a.component.props?.statusChangeConfig
                const itemIdProp =
                    statusChangeConfig?.itemIdProp ||
                    'rtA_GroupToBusinessRoleID'
                const isUpdating = updatingItemId === item[itemIdProp]
                return (
                    <InboxStatusIndicator
                        variant={item?.rtA_CSVInboxItemStatusName}
                        label={t(`${item.rtA_CSVInboxItemStatusName}`)}
                        size="medium"
                        minWidth="100px"
                        tooltipText={
                            a.component.props?.tooltipProp
                                ? item[a.component.props.tooltipProp]
                                : undefined
                        }
                        isLoading={isUpdating}
                        isRowEditable={
                            (typeof isRowEditable === 'function'
                                ? isRowEditable(item)
                                : isRowEditable) ||
                            item?.rtA_CSVInboxItemStatusID === 4
                        }
                        onChange={(newStatus, onSuccess) => {
                            // Build payload if config is provided
                            if (statusChangeConfig && onStatusChange) {
                                const payload = {
                                    [statusChangeConfig.itemIdPayloadKey]:
                                        item[statusChangeConfig.itemIdProp],
                                    [statusChangeConfig.statusIdPayloadKey]:
                                        statusChangeConfig.statusIdMap[
                                            newStatus
                                        ],
                                }
                                // Pass onSuccess callback to update status on success
                                onStatusChange(
                                    payload,
                                    item,
                                    newStatus,
                                    onSuccess,
                                )
                            }
                        }}
                        {...a.component.props}
                    />
                )
            case 'Landscape':
                const landscapeStatusColors = {
                    1: '#1eb77b', // Active - green
                    2: '#3077c1', // Completed - blue
                    3: '#5d6870', // Other - grey
                }
                const statusId =
                    item[a.component.props?.statusIdProp] ||
                    item.rtA_CSVInboxLandscapeStatusID
                const circleColor = landscapeStatusColors[statusId] || '#5d6870'

                return (
                    <Box display="flex" alignItems="center" gap="8px">
                        <Box
                            sx={{
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                backgroundColor: circleColor,
                                flexShrink: 0,
                            }}
                        />
                        <span
                            style={{
                                color: a.styles?.color,
                                fontSize: a.styles?.fontSize,
                            }}
                        >
                            {item[a.name]}
                        </span>
                    </Box>
                )
            case 'RiskIndicator':
                return (
                    <Box
                        display={'flex'}
                        alignItems={'center'}
                        justifyContent={'center'}
                    >
                        {item[a.component.name] ? (
                            <RiskIndicator
                                variant={item[a.component.name]}
                                label={t(`${item[a.component.name]}Risk`)}
                                {...a.component.props}
                            />
                        ) : (
                            <>-</>
                        )}
                    </Box>
                )

            case 'CellDateTime':
                return <CellDateTime value={item[a.name]} />

            case 'SubjectType':
                const subjectType = 'Person' //hardcoded for now as it will always be a person
                return (
                    <SubjectTypeAttribute
                        subjectType={subjectType}
                        item={item}
                        personComponentProps={{ ...a.component.props }}
                    />
                )
            case 'actionButtons': {
                return (
                    <Box
                        style={{
                            display: 'flex',
                            justifyContent: 'end',
                            gap: '1rem',
                        }}
                    >
                        <Box>
                            <Button.Details
                                item={item}
                                onClick={() => {
                                    history.push(
                                        `?${
                                            a.component.props.queryParam
                                        }=${item?.[
                                            a.component.props.idParam
                                        ]?.toString()}`,
                                    )
                                }}
                            />
                        </Box>
                    </Box>
                )
            }

            default:
                const DynamicComponentToRender =
                    DynamicComponents[a.component?.componentName]

                if (!DynamicComponentToRender) return <></>

                return (
                    <DynamicComponentToRender
                        attribute={a}
                        data={{ item }}
                        styles={{ maxHeight: '48px' }}
                    />
                )
        }
    }

    return (
        <React.Fragment>
            <TableRow
                tabIndex={isRowClickable ? 0 : undefined}
                onKeyDown={isRowClickable ? handleKeyDown : undefined}
                role={isRowClickable ? 'button' : undefined}
                aria-label={
                    isRowClickable
                        ? `${t('ViewDetails', 'View details')} - ${
                              item.friendlyName || item.name || item.id
                          }`
                        : undefined
                }
                sx={{
                    backgroundColor: '#ffffff !important',
                    cursor: isRowClickable ? 'pointer' : 'default',
                    borderTopLeftRadius: '8px !important',
                    borderTopRightRadius: '8px !important',
                    borderBottomLeftRadius: showDetails
                        ? '0px !important'
                        : '8px !important',
                    borderBottomRightRadius: showDetails
                        ? '0px !important'
                        : '8px !important',
                    boxShadow: showDetails
                        ? '0 4px 16px 0 rgba(0, 0, 0, 0.05)'
                        : '0 4px 16px 0 rgba(0, 0, 0, 0.08)',
                    minHeight: '40px',

                    // Apply gradient-like borders to cells only when expanded
                    '& >td': {
                        borderTop: showDetails ? '1px solid #8B5BA7' : 'none',
                    },
                    '& >td:first-child': {
                        borderBottomLeftRadius: showDetails
                            ? '0px !important'
                            : '8px !important',
                        borderTopLeftRadius: '8px !important',
                        borderLeft: showDetails ? '1px solid #682a94' : 'none',
                    },
                    '& >td:last-child': {
                        borderBottomRightRadius: showDetails
                            ? '0px !important'
                            : '8px !important',
                        borderTopRightRadius: '8px !important',
                        borderRight: showDetails ? '1px solid #05afe0' : 'none',
                    },

                    '&:hover': {
                        backgroundColor: '#ffffff !important',
                        '& .Details-button': {
                            backgroundColor: colors.primary,
                            color: '#ffffff',
                            cursor: 'pointer',
                        },
                    },
                    '&:focus-visible': {
                        outline: '2px solid #0057A0',
                        outlineOffset: '2px',
                    },
                }}
                {...rest}
            >
                {attributes.map((a, index) => {
                    const isFirstCell = index === 0

                    // ✅ sticky per-cell (NEW)
                    const isStickyStart = a?.sticky === 'start'
                    const isStickyEnd = a?.sticky === 'end'
                    const left = stickyMeta.startOffsetsByIndex[index] ?? 0
                    const right = stickyMeta.endOffsetsByIndex[index] ?? 0

                    return (
                        <CustomTableCell
                            key={item.id + `${index}`}
                            style={a.styles}
                            link={a.link}
                            url={a.url}
                            onClick={
                                isRowClickable ? handleRowClick : undefined
                            }
                            sx={{
                                ...getStickyCellSx({
                                    isStickyStart,
                                    isStickyEnd,
                                    left,
                                    right,
                                    zIndex: 3,
                                }),
                                // keep existing borderRight behavior
                                backgroundColor: '#ffffff',
                            }}
                        >
                            <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="flex-start"
                                paddingX="10px"
                                width={
                                    a.styles?.width ||
                                    (a.isEditable ? '100%' : undefined)
                                }
                            >
                                {/* Show MODIFIED or SAVED label in first cell */}
                                {isFirstCell && (isRowModified || isSaved) && (
                                    <ModifiedLabel isSaved={isSaved}>
                                        <Icon
                                            name={isSaved ? 'Check' : 'Edit'}
                                            color={'#fff'}
                                            style={{
                                                height: '10px',
                                                width: '10px',
                                                marginRight: '4px',
                                            }}
                                        />
                                        {isSaved ? t('Saved') : t('Modified')}
                                    </ModifiedLabel>
                                )}

                                {/* Render editable control if isEditable is true */}
                                {a.isEditable &&
                                item?.rtA_CSVInboxLandscapeStatusID === 1 &&
                                (typeof isRowEditable === 'function'
                                    ? isRowEditable(item)
                                    : isRowEditable) ? (
                                    renderEditableControls(a, item)
                                ) : (
                                    <>
                                        {a.avatar && (
                                            <Person
                                                name={item[a.name]}
                                                imgSrc={getFullImageUrl(
                                                    item[a.imgProp],
                                                )}
                                                tooltipProps={tooltipProps}
                                                style={a?.textStyles}
                                            />
                                        )}

                                        {!a.hideValue && (
                                            <TextFormatter
                                                value={item[a.name]}
                                                tooltipProps={tooltipProps}
                                                style={a.styles}
                                                maxCharacters={maxCharacters}
                                            ></TextFormatter>
                                        )}

                                        {a.button && renderButton(a)}

                                        {a.component &&
                                            renderComponent(a, item)}
                                    </>
                                )}
                            </Box>
                        </CustomTableCell>
                    )
                })}
            </TableRow>

            {/* Expandable Content Row - only rendered when expandable */}
            {isExpandable && (
                <ExpandableRow
                    ref={expandableRowRef}
                    sx={{
                        display: showDetails ? 'table-row' : 'none',
                        // Borders are intentionally NOT placed on the <td> here.
                        // The <td> spans all columns (full scrollable table width),
                        // so any border on it would extend off-screen or shift as the
                        // user scrolls. Instead, borders are applied directly to the
                        // sticky Box below, which is always in the visible viewport.
                    }}
                >
                    <ExpandableContentCell colSpan={attributes.length}>
                        <Collapse in={showDetails} timeout="auto" unmountOnExit>
                            {/*
                             * position:sticky + left:0 pins this box to the
                             * left edge of the scroll container so the detail
                             * panel never scrolls out of view horizontally.
                             * The measured scrollContainerWidth ensures the
                             * content fills exactly the visible area and doesn't
                             * stretch across the full (wider) table width.
                             * Borders live here (not on the <td>) so they always
                             * wrap the visible panel regardless of scroll position.
                             */}
                            <Box
                                ref={expandableContentRef}
                                display="flex"
                                justifyContent="flex-start"
                                sx={{
                                    position: 'sticky',
                                    left: 0,
                                    width: scrollContainerWidth
                                        ? `${scrollContainerWidth}px`
                                        : '100%',
                                    overflow: 'hidden',
                                    borderLeft: '1px solid #682a94',
                                    borderRight: '1px solid #05afe0',
                                    borderBottom: '1px solid #05afe0',
                                    borderTop: '1px solid #0057a0',
                                    borderBottomLeftRadius: '8px',
                                    borderBottomRightRadius: '8px',
                                }}
                            >
                                {expandableComponent &&
                                    expandableComponent(item)}
                            </Box>
                        </Collapse>
                    </ExpandableContentCell>
                </ExpandableRow>
            )}

            <EmptyRow>
                <EmptyCell colSpan={attributes.length} />
            </EmptyRow>
        </React.Fragment>
    )
}
